/**
 * TableSorter Service
 *
 * A reusable, object-oriented service for adding sorting functionality to HTML tables.
 * Follows Service-Oriented Design patterns for modularity and reusability.
 *
 * Features:
 * - Click column headers to sort
 * - Toggle between ascending/descending order
 * - Visual indicators (arrows) for sort direction
 * - Supports multiple data types (string, number, date)
 * - Maintains sort state
 */


type SortOrder = 'asc' | 'desc';
type DataType = 'string' | 'number' | 'date';

interface ColumnConfig {
    index: number;
    type?: DataType;
    sortable?: boolean;
}

interface TableSorterOptions {
    defaultSortColumn?: number | null;
    defaultSortOrder?: SortOrder;
    sortIcon?: string;
    ascIcon?: string;
    descIcon?: string;
}

/**
 * TableSorter class for adding sort functionality to tables
 */
export class TableSorter {
    private tableId: string;
    private table: HTMLTableElement | null;
    private tbody: HTMLTableSectionElement | null;
    private currentSortColumn: number | null;
    private currentSortOrder: SortOrder;
    private sortableColumns: Map<number, DataType>;
    private options: Required<TableSorterOptions>;

    /**
     * Create a TableSorter instance for a specific table
     * @param tableId - The ID of the table element
     * @param options - Configuration options
     */
    constructor(tableId: string, options: TableSorterOptions = {}) {
        this.tableId = tableId;
        this.table = document.getElementById(tableId) as HTMLTableElement | null;
        this.tbody = null;
        this.currentSortColumn = null;
        this.currentSortOrder = 'asc';
        this.sortableColumns = new Map<number, DataType>();

        // Configuration options
        this.options = {
            defaultSortColumn: options.defaultSortColumn ?? null,
            defaultSortOrder: options.defaultSortOrder || 'asc',
            sortIcon: options.sortIcon || '▲▼',
            ascIcon: options.ascIcon || '▲',
            descIcon: options.descIcon || '▼'
        };

        if (!this.table) {
            console.error(`TableSorter: Table with ID '${tableId}' not found`);
            return;
        }

        this.tbody = this.table.querySelector('tbody');
        if (!this.tbody) {
            console.error(`TableSorter: No tbody found in table '${tableId}'`);
            return;
        }
    }

    /**
     * Initialize the table sorter by making headers clickable
     * @param columnConfig - Array of objects: {index: 0, type: 'string', sortable: true}
     */
    public initialize(columnConfig: ColumnConfig[] = []): void {
        const thead = this.table?.querySelector('thead');
        if (!thead) {
            console.error(`TableSorter: No thead found in table '${this.tableId}'`);
            return;
        }

        const headers = thead.querySelectorAll('th');

        columnConfig.forEach(config => {
            if (config.sortable !== false) {
                this.sortableColumns.set(config.index, config.type || 'string');

                const header = headers[config.index];
                if (header) {
                    // Add sorting capability
                    header.style.cursor = 'pointer';
                    header.style.userSelect = 'none';
                    header.classList.add('sortable');

                    // Add sort indicator container (initially hidden)
                    const sortIndicator = document.createElement('span');
                    sortIndicator.className = 'sort-indicator';
                    sortIndicator.style.marginLeft = '5px';
                    sortIndicator.style.fontSize = '10px';
                    sortIndicator.style.display = 'none';
                    sortIndicator.textContent = '';
                    header.appendChild(sortIndicator);

                    // Add click handler
                    header.addEventListener('click', () => this.sortByColumn(config.index));
                }
            }
        });

        console.log(`TableSorter initialized for '${this.tableId}' with ${this.sortableColumns.size} sortable columns`);
    }

    /**
     * Sort the table by a specific column
     * @param columnIndex - The index of the column to sort by
     */
    public sortByColumn(columnIndex: number): void {
        // Determine sort order
        if (this.currentSortColumn === columnIndex) {
            // Toggle order if same column
            this.currentSortOrder = this.currentSortOrder === 'asc' ? 'desc' : 'asc';
        } else {
            // Default to ascending for new column
            this.currentSortOrder = 'asc';
            this.currentSortColumn = columnIndex;
        }

        const dataType = this.sortableColumns.get(columnIndex) || 'string';

        if (!this.tbody) return;

        // Get all rows as array
        const rows = Array.from(this.tbody.querySelectorAll('tr'));

        // Sort rows
        rows.sort((rowA, rowB) => {
            const cellA = rowA.cells[columnIndex];
            const cellB = rowB.cells[columnIndex];

            if (!cellA || !cellB) return 0;

            const valueA = this.extractCellValue(cellA, dataType);
            const valueB = this.extractCellValue(cellB, dataType);

            return this.compareValues(valueA, valueB, dataType, this.currentSortOrder);
        });

        // Re-append rows in sorted order
        rows.forEach(row => this.tbody?.appendChild(row));

        // Update visual indicators
        this.updateSortIndicators(columnIndex);

        console.log(`Sorted table '${this.tableId}' by column ${columnIndex} (${this.currentSortOrder})`);
    }

    /**
     * Extract the value from a table cell based on data type
     * @param cell - The cell to extract value from
     * @param dataType - The type of data ('string', 'number', 'date')
     * @returns The extracted value
     */
    private extractCellValue(cell: HTMLTableCellElement, dataType: DataType): string | number {
        let value = cell.textContent?.trim() || '';

        // Check for data-sort-value attribute (allows custom sort values)
        if (cell.hasAttribute('data-sort-value')) {
            value = cell.getAttribute('data-sort-value') || '';
        }

        switch (dataType) {
            case 'number':
                // Remove commas and parse as float
                return parseFloat(value.replace(/,/g, '')) || 0;

            case 'date':
                // Try to parse as date
                const date = new Date(value);
                return isNaN(date.getTime()) ? 0 : date.getTime();

            case 'string':
            default:
                // Lowercase for case-insensitive sorting
                return value.toLowerCase();
        }
    }

    /**
     * Compare two values based on data type and sort order
     * @param valueA - First value
     * @param valueB - Second value
     * @param dataType - The type of data
     * @param order - Sort order ('asc' or 'desc')
     * @returns Comparison result (-1, 0, 1)
     */
    private compareValues(
        valueA: string | number,
        valueB: string | number,
        _dataType: DataType,
        order: SortOrder
    ): number {
        let comparison = 0;

        if (valueA < valueB) {
            comparison = -1;
        } else if (valueA > valueB) {
            comparison = 1;
        }

        // Reverse for descending order
        return order === 'desc' ? comparison * -1 : comparison;
    }

    /**
     * Update visual indicators to show current sort column and direction
     * @param activeColumnIndex - The currently sorted column index
     */
    private updateSortIndicators(activeColumnIndex: number): void {
        const thead = this.table?.querySelector('thead');
        if (!thead) return;

        const headers = thead.querySelectorAll('th');

        headers.forEach((header, index) => {
            const indicator = header.querySelector('.sort-indicator') as HTMLElement;
            if (indicator) {
                if (index === activeColumnIndex) {
                    // Active column - show direction arrow
                    indicator.style.display = 'inline';
                    indicator.textContent = this.currentSortOrder === 'asc'
                        ? this.options.ascIcon
                        : this.options.descIcon;
                } else {
                    // Inactive columns - hide arrow
                    indicator.style.display = 'none';
                    indicator.textContent = '';
                }
            }
        });
    }

    /**
     * Reset the table to its original unsorted state
     */
    public reset(): void {
        this.currentSortColumn = null;
        this.currentSortOrder = 'asc';

        // Hide all sort indicators
        const thead = this.table?.querySelector('thead');
        if (thead) {
            const headers = thead.querySelectorAll('th');
            headers.forEach(header => {
                const indicator = header.querySelector('.sort-indicator') as HTMLElement;
                if (indicator) {
                    indicator.style.display = 'none';
                    indicator.textContent = '';
                }
            });
        }

        console.log(`Reset sorting for table '${this.tableId}'`);
    }

    /**
     * Get current sort state
     * @returns Current sort state {column, order}
     */
    public getSortState(): { column: number | null; order: SortOrder } {
        return {
            column: this.currentSortColumn,
            order: this.currentSortOrder
        };
    }

    /**
     * Get sort configuration (compatible with SortConfig type)
     * @returns Sort configuration object or null
     */
    public getSortConfig(): SortConfig | null {
        if (this.currentSortColumn === null) {
            return null;
        }

        return {
            field: `column_${this.currentSortColumn}`,
            direction: this.currentSortOrder
        };
    }
}

// Export to global scope
declare global {
    interface Window {
        TableSorter: typeof TableSorter;
    }
}

if (typeof window !== 'undefined') {
    window.TableSorter = TableSorter;
}

console.log('TableSorter service loaded');

export type {
    SortOrder,
    DataType,
    ColumnConfig,
    TableSorterOptions
};
